/**
 * Typed HTTP client for the NestJS API.
 *
 * Default base is the RELATIVE `/api` prefix: in every deployment the ingress
 * routes `/api/*` to the api service and strips the prefix, so the browser
 * talks to the same origin and no API host is baked into the bundle. `VITE_API_URL`
 * is only set for unusual contours — Vite's dev server proxies `/api` to :3001
 * (see vite.config.ts).
 *
 * Envelope + error normalisation lives in `./envelope` (docs/05 §4) and is pure,
 * so it is unit-testable; this file is only the transport around it.
 */

import type { AuthMe, HealthResponse } from '@cb-manager/shared';

import {
  parseErrorBody,
  statusCodeToCode,
  unwrapEnvelope,
  type ApiErrorBody,
  type ApiErrorCode,
  type ApiErrorMeta,
  type Envelope,
  type EnvelopeMeta,
} from './envelope';
import type { MeResponse } from './types';

export type { ApiErrorBody, ApiErrorCode, ApiErrorMeta, Envelope, EnvelopeMeta };
export type { AuthMe, HealthResponse, MeResponse };
export { parseErrorBody, statusCodeToCode, unwrapEnvelope };

// T.Z70 — the optional chain is for the plain-Node `unit` project: Vite injects
// `import.meta.env` in every dev/build/test run it serves, but the unit runner loads
// this file bare, where `env` is undefined and the bare property read would throw at
// IMPORT time — which is exactly how the group wire half (`curator-groups.ts`, the
// chain that answered the owner's fake 500 twice) stayed unpinnable. Browser behavior
// is unchanged: the default stays the relative `/api`.
export const API_BASE_URL = import.meta.env?.VITE_API_URL ?? '/api';

/** An API error with its HTTP status, the documented error code and message. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly method: string,
    readonly path: string,
    readonly code: ApiErrorCode,
    message: string,
    /**
     * ADDITIVE (v1.8.0) — the structured payload some codes carry
     * (`retryAfterSeconds`, `hint`, `limit`). A caller reads it through
     * `error.meta?.…`; absent on every pre-v1.8.0 refusal.
     */
    readonly meta?: ApiErrorMeta,
  ) {
    super(`${method} ${path} failed with HTTP ${status} (${code}): ${message}`);
    this.name = 'ApiError';
  }

  /** True for the documented code (or its HTTP status, as a fallback). */
  is(code: ApiErrorCode, status?: number): boolean {
    return this.code === code || (status !== undefined && this.status === status);
  }

  /** A number out of `meta`, when it is there and really is a number. */
  metaNumber(key: string): number | undefined {
    const value = this.meta?.[key];
    return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
  }
}

/** Anything serialisable into a query string; `undefined`/`null`/'' drop the key. */
export type QueryValue = string | number | boolean | null | undefined;

export function buildQuery(params: object = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params as Record<string, QueryValue>)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const encoded = search.toString();
  return encoded ? `?${encoded}` : '';
}

async function parseError(response: Response): Promise<ApiErrorBody> {
  try {
    return parseErrorBody(await response.json(), response.status);
  } catch {
    const body: ApiErrorBody = {
      code: statusCodeToCode(response.status),
      message: response.statusText || 'неизвестная ошибка',
    };
    return body;
  }
}

/**
 * A 401 means the session is gone. The browser is sent to the OAuth entry
 * point with a FULL page load — the session cookie is set by the api on a
 * redirect chain that starts at oauth.yandex.ru, and a client-side route change
 * would leave the already-mounted queries holding a stale 401.
 *
 * `/auth/me` is excluded: it is the probe that *decides* whether we are logged
 * in, so it must be allowed to answer "no" without bouncing the app.
 */
function handleUnauthorized(path: string): void {
  if (path.startsWith('/auth')) return;
  window.location.assign(`${window.location.origin}${API_BASE_URL}/auth/login`);
}

/**
 * The one fetch every page goes through. Returns the RAW body — a bare object,
 * a named-list envelope or `{ data, meta }` all arrive untouched; use
 * {@link requestEnvelope} when a page wants the payload separated from `meta`.
 */
export async function apiRequest<T>(method: string, path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: init.method ?? method,
    ...init,
    headers: { Accept: 'application/json', ...(init.headers ?? {}) },
  });
  if (!response.ok) {
    if (response.status === 401) handleUnauthorized(path);
    const { code, message, meta } = await parseError(response);
    throw new ApiError(response.status, method, path, code, message, meta);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** `apiRequest` + envelope unwrapping: `{ data, meta? }` out, whatever came in. */
export async function requestEnvelope<T>(
  method: string,
  path: string,
  keys: readonly string[] = ['data'],
  init: RequestInit = {},
): Promise<Envelope<T>> {
  return unwrapEnvelope<T>(await apiRequest<unknown>(method, path, init), keys);
}

export const health = {
  /** GET /healthz — a bare object, no envelope. */
  get: (signal?: AbortSignal) => apiRequest<HealthResponse>('GET', '/healthz', { signal }),
};

/**
 * Session identity. A 401 here is meaningful — the caller decides what to do
 * (the auth context routes to /login, it does not bounce to OAuth).
 */
export const auth = {
  /** GET /auth/me. `role` arrives with the T.B1 RBAC upgrade; until then it is
   *  `undefined` and every role gate fails closed (read-only UI). */
  me: (signal?: AbortSignal) => requestEnvelope<MeResponse>('GET', '/auth/me', ['data'], { signal }),
  /**
   * Full-page navigation: the api destroys the session, then redirects to the
   * `/logged-out` interstitial — never to /auth/login, or the still-live
   * oauth.yandex.ru session makes Yandex answer silently and log the user back
   * in. `<a href>` would work too; assign() keeps it one line.
   */
  logout: () => {
    window.location.assign(`${API_BASE_URL}/auth/logout`);
  },
  /** Where the "Sign in" button on the interstitial / login page goes. */
  loginUrl: `${API_BASE_URL}/auth/login`,
};
